// TODO: add logo availability checks (HEAD), metadata retrieval, preloading and retry logic.
/**
 * Logo Service Class
 */
class LogoService {
  
  /**
   * Get logo URL for a company using logo_uuid
   * @param {Object} company - Company object with logo_uuid
   * @returns {string|null} - Logo URL or null if no logo available
   */
  getLogoUrl(company) {
    if (!company) return null;
    
    if (company.logo_uuid) {
      const baseURL = process.env.REACT_APP_API_BASE_URL || '';
      return `${baseURL}/api/v2/logos/${company.logo_uuid}`;
    }
    
    // Fallback to logo_url if available (for backwards compatibility)
    if (company.logo_url) {
      return company.logo_url;
    }
    
    return null;
  }

  /**
   * Get fallback logo URL using internal avatar generator
   * @param {string} name - Company or person name
   * @param {Object} options - Logo options
   * @param {number} options.size - Logo size in pixels (default: 32)
   * @param {string} options.background - Background color (default: '4a90e2')
   * @param {string} options.color - Text color (default: 'fff')
   * @param {boolean} options.rounded - Whether to use rounded corners (default: true)
   * @param {boolean} options.bold - Whether to use bold text (default: true)
   * @returns {string} - Fallback logo URL
   */
  getFallbackLogoUrl(name, options = {}) {
    const {
      size = 32,
      background = '4a90e2',
      color = 'fff',
      rounded = true,
      bold = true
    } = options;
    
    const encodedName = encodeURIComponent(name || 'Company');
    return `https://ui-avatars.com/api/?name=${encodedName}&size=${size}&background=${background}&color=${color}&rounded=${rounded}&bold=${bold}`;
  }

  /**
   * Get complete logo URL with fallback
   * @param {Object} company - Company object
   * @param {Object} options - Fallback options
   * @returns {string} - Logo URL (internal API or fallback)
   */
  getLogoWithFallback(company, options = {}) {
    const logoUrl = this.getLogoUrl(company);
    if (logoUrl) {
      return logoUrl;
    }
    
    const companyName = company?.name || 'Company';
    return this.getFallbackLogoUrl(companyName, options);
  }

  /**
   * Handle logo loading error
   * @param {Event} event - Error event
   * @param {Object} company - Company object
   * @param {Object} options - Fallback options
   */
  handleLogoError(event, company, options = {}) {
    if (event.target && event.target.src) {
      event.target.src = this.getFallbackLogoUrl(company?.name || 'Company', options);
    }
  }

  /**
   * Create a logo component props object
   * @param {Object} company - Company object
   * @param {Object} options - Logo options
   * @returns {Object} - Props for logo img element
   */
  createLogoProps(company, options = {}) {
    const logoUrl = this.getLogoWithFallback(company, options);
    
    return {
      src: logoUrl,
      alt: `${company?.name || 'Company'} logo`,
      onError: (e) => this.handleLogoError(e, company, options),
      style: {
        objectFit: 'contain',
        ...options.style
      }
    };
  }
}

const logoService = new LogoService();
export default logoService;

export { LogoService };
